import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { popUp } from "../../features/popUp/PopUp";
import { refreshTokenAPI } from "../../processes/api/login";
import { addSecondsToCurrentTime } from "./addSecondsToCurrentTime";
import { secondsToMileSeconds } from "./secondsToMileSeconds";
import { setAuthToken } from "./setAuthToken";
import { deleteAuthToken } from "./deleteAuthToken";

// Simple 401 handling with single-flight refresh and request replay
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
  config: AxiosRequestConfig;
}> = [];

async function refreshAuthTokenNow() {
  try {
    const res = await refreshTokenAPI();
    const newToken = res.data.authData.token;
    const newTokenLife = res.data.authData.tokenExpiresIn - 30;
    // Merge into existing authInfo from localStorage
    const raw = localStorage.getItem("authInfo") || "{}";
    const current = JSON.parse(raw || "{}");
    const newAuthInfo = {
      ...current,
      token: newToken,
      tokenLifeTime: addSecondsToCurrentTime(secondsToMileSeconds(newTokenLife)),
    };
    setAuthToken(JSON.stringify(newAuthInfo));
    return newToken;
  } catch (e) {
    // If refresh fails, clear auth and redirect to login
    deleteAuthToken();
    window.location.href = "/login";
    throw e;
  }
}

export function applyInterceptors(axiosInstance: AxiosInstance) {
  // Error handler
  axiosInstance.interceptors.response.use(
    function (response: AxiosResponse) {
      return response;
    },
    async function (error) {
      const { response, code, config } = error || {};
      const originalRequest: AxiosRequestConfig = config || {};

      if (code === "ERR_NETWORK") {
        popUp({ type: "failure", message: "Lost internet connection" });
        return Promise.reject(error);
      }

      if (response && response.status === 401 && !(originalRequest as any)._retry) {
        // First, try to avoid duplicate refresh calls
        (originalRequest as any)._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            // Force refresh token on 401 instead of time-based check
            const newToken = await refreshAuthTokenNow();
            // Drain queue
            pendingQueue.forEach(({ resolve }) => resolve(newToken));
            pendingQueue = [];
            return axiosInstance(originalRequest);
          } catch (e) {
            pendingQueue.forEach(({ reject }) => reject(e));
            pendingQueue = [];
            return Promise.reject(e);
          } finally {
            isRefreshing = false;
          }
        } else {
          // Queue the request until refresh completes
          return new Promise((resolve, reject) => {
            pendingQueue.push({ resolve, reject, config: originalRequest });
          }).then(() => {
            // After refresh, retry with updated Authorization
            const raw = localStorage.getItem("authInfo") || "{}";
            const data = JSON.parse(raw || "{}");
            const token = data?.token;
            if (token) {
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${token}`,
              } as any;
            }
            return axiosInstance(originalRequest);
          });
        }
      }

      // Non-401 or already retried: show server message if available
      if (response && response.data && response.data.errors && response.data.errors[0]?.message) {
        popUp({ type: "failure", message: response.data.errors[0].message });
      } else {
        popUp({ type: "failure", message: "Something when wrong" });
      }

      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.request.use((config) => {
    const data = JSON.parse(localStorage.getItem("authInfo") || "{}");
    const token = data.token;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });
}
